import { useEffect, useState, useContext, useRef } from "react";
import { useReactToPrint } from "react-to-print";
import { useLocation } from "react-router";
import { LinearProgress } from "@mui/material";
import { toast } from "react-toastify";

import Header from "./../../Component/Header/Header";
import classes from "./Discovery.module.css";
import BrandScore from "./BrandScore/BrandScore";
import CoreAttributes from "./CoreAttributes/CoreAttributes";
import Competitors from "./Competitors/Competitors";
import { DiscoveryContext } from "./discovery-context";
import Sources from "./Sources/Sources";
import Tabs from "./../../Component/UI/Tabs/Tabs";
import Form from "./Form";
import AIGenerated from "../../Component/AIGenerated/AIGenerated";
import MUIButton from "../../Component/UI/MUIButton/MUIButton";
import CategoryDimensions from "./CategoryDimensions/CategoryDimensions";
import History from "./History/History";
import { useDailyLimits } from "../../hooks/useDailyLimits";
import PDFHeader from "../../Component/PDFHeader/PDFHeader";
import PDFFooter from "../../Component/PDFFooter/PDFFooter";
import Taxonomy from "./Taxonomy/Taxonomy";
import api from "../../BrandOnBoarding/api";
import ProductSpecs from "./../CategoryDiscovery/ProductSpecs/ProductSpecs";
import RealtimeActions from "./Actions/Actions";
import { AuthContext } from "../../context/auth-context";
import { ENTITY_LABELS } from "../../utils/labelMapping";

export default function Discovery() {
  const [inputValues, setInputValues] = useState({
    brand: "",
    category: "",
    market: "",
    llm: "",
    type: "brand",
  });
  const [tabSelected, setTabSelected] = useState("overview");
  const [externalReportLoading, setExternalReportLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [warningText, setWarningText] = useState("");
  const [childKey, setChildKey] = useState(0);
  const { user } = useContext(AuthContext);

  const remountChild = () => {
    setChildKey((prev) => prev + 1);
  };

  const {
    launchText,
    updateLaunches,
    launchesRemaining,
    isSpecial,
    frequency: launchLimitFrequency,
  } = useDailyLimits("Discovery");

  const discoveryCtx = useContext(DiscoveryContext);
  const abortControllerRef = useRef(null);
  const location = useLocation();

  const entity = discoveryCtx.entity;
  const discoveryType = discoveryCtx.type || "brand";

  const pdfRef = useRef();
  const rankScoreAbortCtrRef = useRef(null);
  let brandData = discoveryCtx.brand_scores;
  let brandDataCompetitors = brandData.slice(1);
  const highestFavorability = Math.max(...brandDataCompetitors.map((item) => item?.favorability ?? 0));
  const highestSentiment = brandDataCompetitors.find(
    (item) => item?.favorability === highestFavorability
  );

  async function getRankScores() {
    const variants =
      discoveryCtx.type === "product"
        ? discoveryCtx.competitors.map((comp) => {
          return comp?.variants
            ? comp.variants
              .filter((variant) => variant.active)
              .map((variant) => variant.name)
            : [];
        })
        : [];
    rankScoreAbortCtrRef.current = new AbortController();

    const configs = {
      signal: rankScoreAbortCtrRef.current.signal,
    };
    const payload = {
      llm: discoveryCtx.llm,
      category: discoveryCtx.category,
      brand: discoveryCtx.type === "brand" ? "companies" : "products",
      persona: "General Profile",
      market: discoveryCtx.market,
      product: entity,
      language_id: discoveryCtx.language == "it" ? 2 : 1,
      customer_id: user.customer_id,
      rank_exec_count:
        discoveryCtx.type === "category" ||
          discoveryCtx.llm === "deepseek_together"
          ? 1
          : 5,
      competitors: discoveryCtx.competitors
        .filter((item) => item.name != entity)
        .map((item) => item.name),
      variants,
    };
    try {
      const response = await api.post(
        "/metrics/v4/get-brand-index-score?score_type_flag=rank",
        payload,
        configs
      );
      const sources = [...Object.keys(response?.data?.sources?.rank || {})];
      discoveryCtx.updateSources(sources);
      discoveryCtx.updateRankScores(response.data);
    } catch (error) {
      console.log(error);
    }
  }

  useEffect(() => {
    if (discoveryCtx.state.id) return;
    if (discoveryCtx.competitors.length === 4 && discoveryCtx.is_launched) {
      getRankScores();
    }
  }, [
    discoveryCtx.competitors.length,
    discoveryCtx.is_launched,
    discoveryCtx.state.id,
  ]);

  function tabChangeHandler(val) {
    setTabSelected(val);
  }

  useEffect(() => {
    if (location.search && location.search.includes("?id=")) {
      const id = location.search.split("?id=")[1];
      loadDiscovery(id);
    }
    if (discoveryCtx.abort_launch) {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (rankScoreAbortCtrRef.current) {
        rankScoreAbortCtrRef.current.abort();
      }
    }
  }, [discoveryCtx.abort_launch]);

  async function loadDiscovery(id) {
    try {
      setExternalReportLoading(true);
      const response = await api.get(`/users/discovery/${id}`);
      const item = response.data.data;
      let updatedItem = { ...item.json_data, id: item.id };

      let coreAttributeSummary = updatedItem?.core_attributes?.summary ?? "";
      if (!coreAttributeSummary) {
        updatedItem?.core_attributes &&
          updatedItem?.core_attributes?.products &&
          updatedItem?.core_attributes?.products.map((product) => {
            coreAttributeSummary += product.summary;
            coreAttributeSummary += "\n\n";
          });
      }
      discoveryCtx.updateEntireState({
        ...updatedItem,
        core_attributes: {
          ...updatedItem.core_attributes,
          summary: coreAttributeSummary,
        },
        is_launched: true,
        loading: false,
      });
    } catch (error) {
      toast.error(
        "Failed to load discovery report. Please check if URL is correct"
      );
    } finally {
      setExternalReportLoading(false);
    }
  }

  useEffect(() => {
    if (!inputValues.brand || !inputValues.category) return;
    // getCompetitors();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [inputValues]);

  let coreAttributesSummary = "";
  let categoryDimensionsSummary = "";
  let productDimensionSummary = "";
  if (
    discoveryCtx?.core_attributes &&
    Object.keys(discoveryCtx?.core_attributes).length > 0
  ) {
    coreAttributesSummary = discoveryCtx.core_attributes.summary;
  }
  if (
    discoveryCtx?.category_dimensions &&
    Object.keys(discoveryCtx?.category_dimensions).length > 0
  ) {
    categoryDimensionsSummary = discoveryCtx.category_dimensions.summary;
  }

  if (
    discoveryCtx?.taxonomies &&
    Object.keys(discoveryCtx?.taxonomies).length > 0
  ) {
    productDimensionSummary = discoveryCtx.taxonomies.summary;
  }

  let competitors = discoveryCtx.competitors;

  const exportToPDF = useReactToPrint({
    contentRef: pdfRef,
    documentTitle: `${entity} Overview`,
    removeAfterPrint: true,
    pageStyle: `
      @page {
        size: 380mm 400mm;
        margin-left: 10mm;
        margin-right: 10mm;
        margin-bottom: 5mm;
        margin-top: 15mm;
      }
      @media print {
        body {
          -webkit-print-color-adjust: exact; /* Ensures accurate color printing */
        }
        .page-footer {
          position: fixed;
          bottom: 10mm;
          left: 10mm;
          right: 10mm;
          font-size: 10pt;
          color: #777;
          text-align: center;
        }
      }
    `,
  });

  async function generateReport() {
    try {
      setLoading(true);
      const frontend_build_version = localStorage.getItem(
        "frontend_build_version"
      );
      const token = localStorage.getItem("token");
      const user = localStorage.getItem("user");
      const payload = {
        isBrand: discoveryType === "brand",
        request_type: "discovery",
        transactionID:discoveryCtx.state.id,
        CompetitorName: highestSentiment.name,
        brand: entity,
        category: discoveryCtx.category,
        market: discoveryCtx.market,
        primaryLLM: discoveryCtx.llm,
        frontend_build_version,
        token,
        user,
      };
      console.log(payload);

      const blob = await api.post(
        "/llm-reporting/generate/discovery",
        payload,
        {
          responseType: "blob",
        }
      );
      const url = window.URL.createObjectURL(blob.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "Discovery.pptx";
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      {externalReportLoading && <LinearProgress />}
      <section
        className={classes.content_section}
        style={{
          gridTemplateRows: discoveryType === "category" && "auto 1fr",
        }}
      >
        <Header actionText={launchText} actionVariant="text" />
        <div
          className={classes.container}
          style={{
            gridTemplateColumns: discoveryType === "category" && "1fr",
          }}
        >
          <div className={classes.content}>
            <Tabs
              options={[]}
              onChange={tabChangeHandler}
              selected={tabSelected}
            />
            <Form
              warningText={warningText}
              setWarningText={setWarningText}
              id="discovery"
              onReset={() => {
                setInputValues((prevValues) => {
                  return {
                    ...prevValues,
                    brand: "",
                    category: "",
                    market: "",
                    llm: "",
                  };
                });
                setWarningText("");
              }}
              onSubmit={(values) => {
                if (launchesRemaining == 0 && !isSpecial) {
                  toast.warn(
                    `You have reached your ${launchLimitFrequency} limit`
                  );
                  return;
                }
                if (!isSpecial) updateLaunches();
                if (externalReportLoading) return;
                if (values.type !== "category") {
                  discoveryCtx.updateEntity(values.brand);
                }
                discoveryCtx.updateMarket(values.market);
                discoveryCtx.updateCategory(values.category);
                discoveryCtx.updateLLM(values.llm);
                discoveryCtx.updateType(discoveryCtx.type);
                discoveryCtx.updateName(`${values.brand} ${ENTITY_LABELS.brand} Overview`);
                setTimeout(() => {
                  discoveryCtx.updatedLaunched(true);
                  discoveryCtx.updateLoading(true);
                  setInputValues(values);
                }, 500);
              }}
              onTypeChanged={(val) => {
                setInputValues((prevVals) => {
                  return {
                    ...prevVals,
                    type: val,
                  };
                });
              }}
            />
          </div>
          {discoveryType !== "category" && (
            <div className={classes.history}>
              <History key={childKey} filter={discoveryCtx.type} />
            </div>
          )}
        </div>
        {discoveryCtx.is_launched && (
          <div className={classes.result} id="print-pdf" ref={pdfRef}>
            <PDFHeader />
            {discoveryType !== "category" && (
              <BrandScore
                category={inputValues.category}
                market={inputValues.market}
                entity={inputValues.brand}
                remountChild={remountChild}
              />
            )}
            <div className="page-break print-only" />
            <div className={classes.section}>
              {competitors.length > 0 && (
                <Competitors
                  category={inputValues.category}
                  entity={inputValues.brand}
                />
              )}
            </div>
            {discoveryType === "brand" && discoveryCtx?.core_attributes && (
              <div className={classes.section_summary}>
                <div className={classes.flex_one}>
                  {competitors.length > 0 && (
                    <CoreAttributes
                      category={inputValues.category}
                      entity={entity}
                    />
                  )}
                </div>
                <div className={classes.flex_one}>
                  <AIGenerated
                    edit
                    text={coreAttributesSummary}
                    loading={coreAttributesSummary === ""}
                    firebaseNode="core_attributes"
                  />
                </div>
              </div>
            )}

            {discoveryType === "category" && (
              <div className={classes.section_summary}>
                <div className={classes.flex_one}>
                  {competitors && competitors.length > 0 && (
                    <CategoryDimensions category={inputValues.category} />
                  )}
                </div>
                <div className={classes.flex_one}>
                  <AIGenerated
                    edit={discoveryType != "category" || !discoveryCtx.loading}
                    text={categoryDimensionsSummary}
                    loading={categoryDimensionsSummary === ""}
                    firebaseNode="category_dimensions"
                  />
                </div>
              </div>
            )}

            {(discoveryType === "product" || discoveryType === "category") && (
              <div className={classes.section_summary}>
                <div className={classes.flex_one}>
                  {competitors && competitors.length > 0 && <Taxonomy />}
                </div>
                <div className={classes.flex_one}>
                  <AIGenerated
                    edit={discoveryType != "category" || !discoveryCtx.loading}
                    text={productDimensionSummary}
                    loading={productDimensionSummary === ""}
                    firebaseNode="taxonomies"
                  />
                </div>
              </div>
            )}

            {discoveryType === "category" && (
              <div className={classes.product_recommendation_container}>
                {competitors && competitors.length > 0 && <ProductSpecs />}
              </div>
            )}

            <div className="page-break print-only" />
            {discoveryType != "category" && (
              <div className={classes.section_summary}>
                <div className={classes.flex_one}>
                  {competitors && competitors.length > 0 && (
                    <CategoryDimensions
                      category={inputValues.category}
                      entity={entity}
                    />
                  )}
                </div>
                <div className={classes.flex_one}>
                  <AIGenerated
                    edit={!discoveryCtx.loading}
                    text={categoryDimensionsSummary}
                    loading={categoryDimensionsSummary === ""}
                    firebaseNode="category_dimensions"
                  />
                </div>
              </div>
            )}
            <div className="page-break print-only" />
            {discoveryType != "category" && (
              <div className={classes.section}>
                <Sources />
              </div>
            )}
            {discoveryType !== "category" && (
              <div className={classes.section}>
                <RealtimeActions />
              </div>
            )}
            {!discoveryCtx.loading && discoveryType != "category" && (
              <div className={`${classes.action_btn_group} no-print`}>
                <MUIButton onClick={exportToPDF}>Export</MUIButton>
                <MUIButton onClick={generateReport} loading={loading}>
                  Generate Report
                </MUIButton>
              </div>
            )}
            <PDFFooter />
          </div>
        )}
      </section>
    </>
  );
}
